import ClientTabHeading from "../clients/ClientTabHeading";
import { clientInvoices } from "../../utils/clientInvoices";
import { isVacationContract } from "../../utils/contractService";
import { highestContractFirst } from "../../utils/contractSort";
import DuplicateContractModal from "./DuplicateContractModal";
import { contractDate } from "../../utils/planningDates";
import PageHeading from "../../components/PageHeading";
import SearchPending from "../../components/SearchPending";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { message } from "antd";
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Download,
  Loader2,
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";

import { createContractSearch } from "../../utils/contractSearch";
import {
  contractDocumentName,
  loadContractPdf,
  presentContractPdf,
} from "../../utils/contractDocument";
import {
  getContractStatus,
  CONTRACT_STATUSES,
} from "../../utils/contractStatus";

const contractFrenchDate = (value) =>
  contractDate(value)?.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) || "—";
const contractNumber = (value) =>
  value == null ||
  String(value).trim() === "" ||
  !Number.isFinite(Number(String(value).replace(",", ".")))
    ? "—"
    : new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(
        Number(String(value).replace(",", ".")),
      );

export default function ContratPage({ audience = "candidate", client, embedded = false, onCreateContract }) {
  const [creating, setCreating] = useState(false);
  const [serviceTab, setServiceTab] = useState("interim");
  const vacationView = audience === "client" && serviceTab === "vacation";
  const [loadedContrats, setContrats] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(searchTerm);
  const [searchType, setSearchType] = useState("all");
  const [currentTab, setCurrentTab] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [loadingActionId, setLoadingActionId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get("pld-contract/freelance", { signal: controller.signal })
      .then((res) => {
        setContrats(
          res.data
            .map((contrat) => ({
              ...contrat,
              statut: getContractStatus(contrat),
            }))
            .sort(highestContractFirst),
        );
      })
      .catch((error) => {
        const detail = apiErrorMessage(error, "Impossible de charger les contrats.");
        if (detail) setLoadError(detail);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, []);

  const contrats =
    audience === "prolongation" || client
      ? clientInvoices(loadedContrats, client)
      : loadedContrats;
  const openContractPdf = (contrat, mode) => {
    setLoadingActionId(contrat._id);
    loadContractPdf(axios, contrat)
      .then((blob) => presentContractPdf(blob, `${contractDocumentName(contrat)}.pdf`, mode))
      .catch((error) =>
        message.error(
          apiErrorMessage(
            error,
            mode === "download"
              ? "Impossible de télécharger ce contrat"
              : "Impossible d'afficher ce contrat",
          ),
        ),
      )
      .finally(() => setLoadingActionId(null));
  };
  const viewContract = (contrat) => openContractPdf(contrat, "view");
  const downloadContract = (contrat) => openContractPdf(contrat, "download");

  const matchesSearch = createContractSearch(appliedSearch, searchType);
  const filteredContrats = contrats.filter((c) => {
    if (
      audience === "client" &&
      (serviceTab === "vacation") !== isVacationContract(c)
    )
      return false;
    if (currentTab !== "all" && c.statut !== CONTRACT_STATUSES[currentTab])
      return false;
    return matchesSearch(c);
  });
  if (vacationView) {
    const latestDate = (contract) => {
      const dates =
        Array.isArray(contract.dates) && contract.dates.length
          ? contract.dates.map((value) =>
              typeof value === "object" ? value?.date : value,
            )
          : [contract.DateDebut];
      return Math.max(
        0,
        ...dates.map((value) => contractDate(value)?.getTime() || 0),
      );
    };
    filteredContrats.sort(
      (a, b) => latestDate(b) - latestDate(a) || highestContractFirst(a, b),
    );
  } else if (audience === "candidate" || audience === "client")
    filteredContrats.sort(highestContractFirst);

  const selectable = filteredContrats.filter((c) => c._id);
  const allSelected =
    selectable.length > 0 &&
    selectable.every((c) => selectedIds.includes(c._id));
  const toggleContract = (id) =>
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  const downloadSelected = async () => {
    if (bulkBusy || !selectedIds.length) return;
    const chosen = contrats.filter((c) => selectedIds.includes(c._id));
    setBulkBusy(true);
    setBulkProgress(0);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const failures = [];
      for (let index = 0; index < chosen.length; index++) {
        const contract = chosen[index];
        try {
          const data = await loadContractPdf(axios, contract);
          const number = contractDocumentName(contract).replace(
            /[^a-zA-Z0-9._-]/g,
            "_",
          );
          zip.file(`${number}.pdf`, data);
        } catch (error) {
          failures.push(`${contract.NumeroContrat || contract._id} (${apiErrorMessage(error, "téléchargement refusé")})`);
        }
        setBulkProgress(index + 1);
      }
      if (failures.length < chosen.length) {
        const blob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "contrats_selectionnes.zip";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        message.success(
          `${chosen.length - failures.length} contrat(s) téléchargé(s).`,
        );
      }
      if (failures.length)
        message.error(`Contrats non téléchargés : ${failures.join(", ")}`, 8);
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible de préparer le téléchargement. Réessayez."));
    } finally {
      setBulkBusy(false);
    }
  };

  const stats = {
    total: contrats.length,
    vacations: contrats.filter(isVacationContract).length,
    paper: contrats.filter((c) => c.statut === CONTRACT_STATUSES.paper).length,
    unsigned: contrats.filter((c) => c.statut === CONTRACT_STATUSES.unsigned)
      .length,
    signed: contrats.filter((c) => c.statut === CONTRACT_STATUSES.signed)
      .length,
    nonLieu: contrats.filter((c) => c.statut === CONTRACT_STATUSES.nonLieu)
      .length,
  };

  // Fonction pour obtenir l'icône et la couleur du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case CONTRACT_STATUSES.unsigned:
        return <Clock className="w-4 h-4 text-blue-600" />;
      case CONTRACT_STATUSES.signed:
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case CONTRACT_STATUSES.paper:
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case CONTRACT_STATUSES.nonLieu:
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <XCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case CONTRACT_STATUSES.unsigned:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case CONTRACT_STATUSES.signed:
        return "bg-green-100 text-green-800 border-green-200";
      case CONTRACT_STATUSES.paper:
        return "bg-orange-100 text-orange-800 border-orange-200";
      case CONTRACT_STATUSES.nonLieu:
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header Section */}
        <motion.div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              {embedded ? <ClientTabHeading title="Contrats de mise à disposition" description="Consultez les contrats du client et leurs conditions de facturation." /> : (
              <PageHeading
                icon="contracts"
                title={
                  audience === "prolongation"
                    ? "Prolongations"
                    : audience === "client"
                      ? "Contrats clients"
                      : "Contrats intérimaires"
                }
                description={
                  audience === "prolongation"
                    ? "Consultez les contrats pour préparer leurs prolongations."
                    : audience === "candidate"
                      ? "Consultez le registre de tous les contrats intérimaires."
                      : "Gérez vos contrats de placements et de missions"
                }
              />
              )}
            </div>
            {audience !== "prolongation" && (!embedded || onCreateContract) && (
              <motion.button
                type="button"
                onClick={() => onCreateContract ? onCreateContract() : setCreating(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Nouveau contrat</span>
              </motion.button>
            )}
          </div>
        </motion.div>

        {creating && (
          <DuplicateContractModal onClose={() => setCreating(false)} />
        )}
        {/* Statistics Cards */}
        {!(embedded && audience === "client") && (
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">
                  Total contrats
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats.total}
                </p>
                {audience === "client" && (
                  <p className="mt-1 text-sm text-slate-500">
                    Dont {stats.vacations} vacation
                    {stats.vacations > 1 ? "s" : ""}
                  </p>
                )}
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">
                  Dématérialisés non signés
                </p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {stats.unsigned}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Signés</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {stats.signed}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">
                  Non dématérialisés
                </p>
                <p className="text-3xl font-bold text-orange-500 mt-1">
                  {stats.paper}
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </motion.div>
        </motion.div>
        )}

        {audience === "client" && (
          <div
            role="tablist"
            aria-label="Type de contrat client"
            className="mb-4 flex gap-2 border-b border-slate-200"
          >
            {[
              { id: "interim", label: "Intérim" },
              { id: "vacation", label: "Vacation" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={serviceTab === tab.id}
                onClick={() => setServiceTab(tab.id)}
                className={`border-b-2 px-4 py-3 text-sm font-semibold ${serviceTab === tab.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              >
                {tab.label}{" "}
                <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                  {
                    contrats.filter(
                      (contract) =>
                        isVacationContract(contract) ===
                        (tab.id === "vacation"),
                    ).length
                  }
                </span>
              </button>
            ))}
          </div>
        )}
        {/* Filters and Search */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: "all", label: "Tous", count: stats.total },
                {
                  id: "paper",
                  label: CONTRACT_STATUSES.paper,
                  count: stats.paper,
                },
                {
                  id: "unsigned",
                  label: CONTRACT_STATUSES.unsigned,
                  count: stats.unsigned,
                },
                {
                  id: "signed",
                  label: CONTRACT_STATUSES.signed,
                  count: stats.signed,
                },
                {
                  id: "nonLieu",
                  label: CONTRACT_STATUSES.nonLieu,
                  count: stats.nonLieu,
                },
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setCurrentTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                    currentTab === tab.id
                      ? "bg-slate-800 text-white shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <span>{tab.label}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      currentTab === tab.id
                        ? "bg-white/20 text-white"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                </motion.button>
              ))}
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Rechercher un contrat"
                  placeholder="Numéro, client, intérimaire…"
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200 w-full sm:w-64"
                />
                <div className="mt-1"><SearchPending pending={searching} /></div>
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200 appearance-none bg-white"
                >
                  <option value="all">Tous les champs</option>
                  <option value="client">Client</option>
                  <option value="candidate">Intérimaire</option>
                  <option value="numero">N° Contrat</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-600">
            {selectedIds.length} contrat(s) sélectionné(s)
          </span>
          <button
            type="button"
            disabled={bulkBusy || !selectedIds.length}
            onClick={downloadSelected}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            <Download size={16} />
            {bulkBusy
              ? `Préparation ${bulkProgress}/${selectedIds.length}…`
              : "Télécharger la sélection"}
          </button>
          {selectedIds.length > 0 && (
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => setSelectedIds([])}
              className="text-sm text-slate-500 hover:underline"
            >
              Effacer la sélection
            </button>
          )}
        </div>
        {/* Contracts Table */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-slate-600">
                Chargement des contrats...
              </span>
            </div>
          ) : loadError ? (
            <p role="alert" className="p-8 text-center text-rose-700">{loadError}</p>
          ) : filteredContrats.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Aucun contrat trouvé
              </h3>
              <p className="text-slate-500">
                {searchTerm
                  ? "Essayez de modifier vos critères de recherche"
                  : "Commencez par créer votre premier contrat"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          aria-label="Sélectionner tous les contrats filtrés"
                          checked={allSelected}
                          disabled={bulkBusy || !selectable.length}
                          style={{ width: "16px" }}
                          onChange={(e) =>
                            setSelectedIds((current) =>
                              e.target.checked
                                ? [
                                    ...new Set([
                                      ...current,
                                      ...selectable.map((c) => c._id),
                                    ]),
                                  ]
                                : current.filter(
                                    (id) =>
                                      !selectable.some((c) => c._id === id),
                                  ),
                            )
                          }
                        />
                        Contrat
                      </label>
                    </th>
                    {(vacationView
                      ? ["Entreprise", "Intérimaire & qualif.", "Dates"]
                      : embedded && audience === "client"
                        ? ["Période", "Fin de mission réelle", "Intérimaire", "Poste"]
                        : [
                          "Période",
                          "Fin de mission réelle",
                          "Client",
                          "Intérimaire",
                          "Poste",
                          "Taux payé",
                        ]
                    ).map((label) => (
                      <th
                        key={label}
                        className="px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
                      >
                        {label}
                      </th>
                    ))}
                    {audience === "client" &&
                      !vacationView &&
                      ["Coeff", "Taux facturé"].map((label) => (
                        <th
                          key={label}
                          className="px-4 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
                        >
                          {label}
                        </th>
                      ))}
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  <AnimatePresence>
                    {filteredContrats.map((contrat, index) => (
                      <motion.tr
                        key={contrat._id || index}
                        className="hover:bg-slate-50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              aria-label={`Sélectionner le contrat ${contrat.NumeroContrat || contrat._id}`}
                              disabled={bulkBusy || !contrat._id}
                              checked={selectedIds.includes(contrat._id)}
                              onChange={() => toggleContract(contrat._id)}
                              className="mr-3 shrink-0"
                              style={{ width: "16px" }}
                            />
                            <div className="bg-slate-100 p-2 rounded-lg mr-3">
                              <FileText className="w-4 h-4 text-slate-600" />
                            </div>
                            <div className="text-sm font-medium text-slate-900">
                              {vacationView
                                ? `#${contractDocumentName(contrat)}`
                                : contractDocumentName(contrat)}
                              {vacationView && (
                                <p className="mt-1 text-xs font-normal text-slate-400">
                                  Vacation
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        {vacationView ? (
                          <>
                            <td className="px-4 py-3 text-sm text-slate-900">
                              <p className="font-medium">
                                {contrat.RaisonSociale || "—"}
                              </p>
                              <p className="mt-1 text-xs text-slate-400">
                                {contrat.AdresseVille || contrat.Ville || ""}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-900">
                              <p className="font-medium">
                                {contrat.NomPrenom || "Non assigné"}
                              </p>
                              <p className="mt-1 text-xs text-slate-400">
                                {contrat.TxtQualification ||
                                  contrat.Qualification ||
                                  "—"}
                              </p>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                              {Array.isArray(contrat.dates) &&
                              contrat.dates.length ? (
                                <>
                                  <p>
                                    {contrat.dates.length} date
                                    {contrat.dates.length > 1 ? "s" : ""}
                                  </p>
                                  <p className="mt-1 text-xs text-slate-400">
                                    {contrat.dates
                                      .map((value) =>
                                        contractFrenchDate(
                                          typeof value === "object"
                                            ? value.date
                                            : value,
                                        ),
                                      )
                                      .join(" · ")}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p>{contractFrenchDate(contrat.DateDebut)}</p>
                                  {contrat.DateFin &&
                                    contrat.DateFin !== contrat.DateDebut && (
                                      <p className="mt-1 text-xs text-slate-400">
                                        au {contractFrenchDate(contrat.DateFin)}
                                      </p>
                                    )}
                                </>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                              <p>Du {contractFrenchDate(contrat.DateDebut)}</p>
                              <p>Au {contractFrenchDate(contrat.DateFin)}</p>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                              {contractFrenchDate(contrat.DateFinMission)}
                            </td>
                            {!(embedded && audience === "client") && (
                            <td className="px-4 py-3 text-sm text-slate-900">
                              <p
                                className="max-w-52 truncate"
                                title={contrat.RaisonSociale}
                              >
                                {contrat.RaisonSociale || "—"}
                              </p>
                            </td>
                            )}
                            <td className="px-4 py-3 text-sm text-slate-900">
                              <p
                                className="max-w-52 truncate"
                                title={contrat.NomPrenom}
                              >
                                {contrat.NomPrenom || "Non assigné"}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              <p
                                className="max-w-44 truncate"
                                title={
                                  contrat.TxtQualification ||
                                  contrat.Qualification
                                }
                              >
                                {contrat.TxtQualification ||
                                  contrat.Qualification ||
                                  "—"}
                              </p>
                            </td>
                            {!(embedded && audience === "client") && (
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                              {contractNumber(
                                contrat.TauxPaye ?? contrat.tauxHoraire,
                              )}
                            </td>
                            )}
                          </>
                        )}
                        {audience === "client" && !vacationView && (
                          <>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                              {contractNumber(
                                contrat.CoeffHeures ?? contrat.coefficient,
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                              {contractNumber(contrat.TauxFacture)}
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {contrat.statut !== CONTRACT_STATUSES.unknown && (
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(contrat.statut)}`}
                            >
                              {getStatusIcon(contrat.statut)}
                              <span className="ml-1 capitalize">
                                {contrat.statut}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-2">
                            {loadingActionId === contrat._id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                            ) : (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => viewContract(contrat)}
                                  className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                  title="Voir le contrat"
                                >
                                  <Eye className="w-4 h-4" />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => downloadContract(contrat)}
                                  className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                                  title="Télécharger"
                                >
                                  <Download className="w-4 h-4" />
                                </motion.button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
